import gql from "graphql-tag";

// fragment
export const GROWTH_TASK_FRAGMENT = gql`
    fragment growthTask on GrowthTaskNode {
        id
        rawId
        note
        numPhases
        plant {
            id
            name
            plantSpecies {
                id
                name
            }
        }
        color {
            id
            color
        }
        potSize {
            id
            potSizeCm
        }
        environment {
            id
            environment
        }
        tag {
            id
            tag
        }
        plantGrowthTemplate {
            id
            name
        }
        supplier {
            id
            name
        }
        totalGrowthDays
        maxSalesDays
        growthStartDate
        growthEndDate
        salesStartDate
        salesEndDate
        lockedDate
        lockedComment
        lockedForCustomer {
            id
            name
        }
        closedDate
        closedReason
        supplierOrderNo
        totalQuantity
        totalInventory
        unitPurchasePrice
        currency
        growthTaskPhaseSet(isCurrentPhase: true) {
            edges {
                node {
                    id
                    phaseNo
                    startDate
                    endDate
                    numDays
                    numPlantsPerM2
                    spaceM2
                    isSalesPhase
                    isCurrentPhase
                    greenhouseDepartmentSection {
                        id
                        name
                        greenhouseDepartment {
                            id
                            name
                            greenhouse {
                                id
                                name
                            }
                        }
                    }
                }
            }
        }
        updatedAt
        updatedBy
    }
`;

export const SINGLE_GROWTH_TASK_FRAGMENT = gql`
    fragment singleGrowthTask on GrowthTaskNode {
        id
        rawId
        note
        numPhases
        plant {
            id
            name
            plantSpecies {
                id
                name
            }
            plantTagSet {
                edges {
                    node {
                        id
                        tag {
                            id
                            tag
                            orderId
                        }
                    }
                }
            }
        }
        color {
            id
            color
        }
        potSize {
            id
            potSizeCm
        }
        environment {
            id
            environment
        }
        tag {
            id
            tag
        }
        plantGrowthTemplate {
            id
            name
        }
        supplier {
            id
            name
        }
        totalGrowthDays
        maxSalesDays
        growthStartDate
        growthEndDate
        salesStartDate
        salesEndDate
        lockedDate
        lockedComment
        lockedForCustomer {
            id
            name
        }
        closedDate
        closedReason
        supplierOrderNo
        totalQuantity
        totalInventory
        unitPurchasePrice
        currency
        growthTaskPhaseSet {
            edges {
                node {
                    id
                    phaseNo
                    startDate
                    endDate
                    numDays
                    numPlantsPerM2
                    spaceM2
                    isSalesPhase
                    isCurrentPhase
                    remindedDate
                    confirmedDate
                    greenhouseDepartmentSection {
                        id
                        name
                        greenhouseDepartment {
                            id
                            name
                        }
                    }
                }
            }
        }
        growthTaskInventoryLogSet(orderBy: "-createdAt") {
            edges {
                node {
                    id
                    createdAt
                    updatedBy
                    change
                    changeReason
                    inventoryAfterChange
                    refType
                    refId
                    comment
                }
            }
        }
        createdAt
        updatedAt
        updatedBy
    }
`;

// all query
export const GROWTH_TASKS_QUERY = gql`
    query {
        tenantGrowthTasks(closedDate_Isnull: true) {
            edges {
                cursor
                node {
                    ...growthTask
                }
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;

// single query
export const SINGLE_GROWTH_TASK_QUERY = gql`
    query growthTask($id: ID!) {
        growthTask(id: $id) {
            ...singleGrowthTask
        }
    }
    ${SINGLE_GROWTH_TASK_FRAGMENT}
`;

// create task
export const CREATE_GROWTH_TASK_MUTATION = gql`
    mutation createGrowthTask($input: CreateGrowthTaskInput!) {
        createGrowthTask(input: $input) {
            growthTask {
                ...growthTask
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;

// update task
export const UPDATE_GROWTH_TASK_MUTATION = gql`
    mutation updateGrowthTask($input: UpdateGrowthTaskInput!) {
        updateGrowthTask(input: $input) {
            growthTask {
                ...growthTask
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;

// update task phases
export const UPDATE_GROWTH_TASK_PHASES_MUTATION = gql`
    mutation updateGrowthTaskPhases($input: UpdateGrowthTaskPhasesInput!) {
        updateGrowthTaskPhases(input: $input) {
            growthTask {
                ...growthTask
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;

// delete
export const DELETE_GROWTH_TASK_MUTATION = gql`
    mutation deleteGrowthTask($input: DeleteGrowthTaskInput!) {
        deleteGrowthTask(input: $input) {
            growthTask {
                ...growthTask
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;

// update task tag
export const UPDATE_GROWTH_TASK_TAG_MUTATION = gql`
    mutation updateGrowthTaskTag($input: UpdateGrowthTaskTagInput!) {
        updateGrowthTaskTag(input: $input) {
            growthTask {
                ...growthTask
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;

// confirm task phase
export const CONFIRM_GROWTH_TASK_PHASE_MUTATION = gql`
    mutation confirmGrowthTaskPhase($input: ConfirmGrowthTaskPhaseInput!) {
        confirmGrowthTaskPhase(input: $input) {
            growthTask {
                ...growthTask
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;

// lock task
export const LOCK_GROWTH_TASK_MUTATION = gql`
    mutation lockGrowthTask($input: LockGrowthTaskInput!) {
        lockGrowthTask(input: $input) {
            growthTask {
                ...growthTask
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;

// unlock task
export const UNLOCK_GROWTH_TASK_MUTATION = gql`
    mutation unlockGrowthTask($input: UnlockGrowthTaskInput!) {
        unlockGrowthTask(input: $input) {
            growthTask {
                ...growthTask
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;

// split task
export const SPLIT_GROWTH_TASK_MUTATION = gql`
    mutation splitGrowthTask($input: SplitGrowthTaskInput!) {
        splitGrowthTask(input: $input) {
            currentGrowthTask {
                ...growthTask
            }
            newGrowthTask {
                ...growthTask
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;

// update supplier
export const UPDATE_GROWTH_TASK_SUPPLIER_MUTATION = gql`
    mutation updateGrowthTaskSupplier($input: UpdateGrowthTaskSupplierInput!) {
        updateGrowthTaskSupplier(input: $input) {
            growthTask {
                ...growthTask
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;

// change inventory
export const UPDATE_GROWTH_TASK_INVENTORY_MUTATION = gql`
    mutation updateGrowthTaskInventory(
        $input: UpdateGrowthTaskInventoryInput!
    ) {
        updateGrowthTaskInventory(input: $input) {
            growthTask {
                ...growthTask
            }
        }
    }
    ${GROWTH_TASK_FRAGMENT}
`;
